import { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { FolderKanban, CheckCircle2, XCircle, Clock } from 'lucide-react'

const Invito = () => {
  const { token } = useParams()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [stato, setStato] = useState('loading') // loading | valido | scaduto | usato | già_membro | errore | accettato
  const [invito, setInvito] = useState(null)
  const [progetto, setProgetto] = useState(null)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (authLoading || !user) return
    verificaInvito()
  }, [authLoading, user]) // eslint-disable-line react-hooks/exhaustive-deps

  const verificaInvito = async () => {
    const { data: inv, error } = await supabase
      .from('project_invites')
      .select('*, progetti(id, titolo, descrizione, colore, status)')
      .eq('token', token)
      .single()

    if (error || !inv) { setStato('errore'); return }

    if (inv.used_by) { setStato('usato'); return }
    if (new Date(inv.expires_at) < new Date()) { setStato('scaduto'); return }

    // Controlla se l'utente è già membro
    const { data: membership } = await supabase
      .from('project_members')
      .select('id')
      .eq('project_id', inv.project_id)
      .eq('user_id', user.id)
      .maybeSingle()

    if (membership) { setStato('già_membro'); setProgetto(inv.progetti); return }

    setInvito(inv)
    setProgetto(inv.progetti)
    setStato('valido')
  }

  const handleAccetta = async () => {
    setAccepting(true)
    const { error: memberError } = await supabase
      .from('project_members')
      .insert({ project_id: invito.project_id, user_id: user.id, role: invito.role })

    if (memberError) { setStato('errore'); setAccepting(false); return }

    await supabase
      .from('project_invites')
      .update({ used_by: user.id, used_at: new Date().toISOString() })
      .eq('id', invito.id)

    setStato('accettato')
    setTimeout(() => navigate('/progetti'), 2000)
  }

  // Non loggato → redirect a login con return URL
  if (!authLoading && !user) {
    return <Navigate to="/login" state={{ from: `/invito/${token}` }} />
  }

  if (authLoading || stato === 'loading') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const roleLabel = { owner: 'Proprietario', editor: 'Editor', viewer: 'Visualizzatore' }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white tracking-tight">Plutonic</h1>
          <p className="text-gray-500 text-sm mt-1">Studio Management</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">

          {/* Valido */}
          {stato === 'valido' && progetto && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: progetto.colore + '33' }}>
                  <FolderKanban size={20} style={{ color: progetto.colore }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Invito al progetto</p>
                  <h2 className="text-lg font-bold text-white">{progetto.titolo}</h2>
                </div>
              </div>

              {progetto.descrizione && (
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">{progetto.descrizione}</p>
              )}

              <div className="bg-gray-800/60 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
                <span className="text-sm text-gray-400">Il tuo ruolo</span>
                <span className="text-sm font-medium text-violet-400">{roleLabel[invito.role] || invito.role}</span>
              </div>

              <button
                onClick={handleAccetta}
                disabled={accepting}
                className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium py-3 rounded-xl transition-colors"
              >
                {accepting ? 'Accettando...' : 'Accetta invito'}
              </button>
            </>
          )}

          {/* Accettato */}
          {stato === 'accettato' && (
            <div className="text-center py-4">
              <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Sei dentro!</h2>
              <p className="text-gray-400 text-sm">Benvenuto nel progetto. Reindirizzamento in corso...</p>
            </div>
          )}

          {/* Già membro */}
          {stato === 'già_membro' && progetto && (
            <div className="text-center py-4">
              <CheckCircle2 size={48} className="text-violet-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Sei già membro</h2>
              <p className="text-gray-400 text-sm mb-6">Fai già parte di <span className="text-white font-medium">{progetto.titolo}</span>.</p>
              <button
                onClick={() => navigate('/progetti')}
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-medium py-3 rounded-xl transition-colors"
              >
                Vai ai progetti
              </button>
            </div>
          )}

          {/* Scaduto */}
          {stato === 'scaduto' && (
            <div className="text-center py-4">
              <Clock size={48} className="text-yellow-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Link scaduto</h2>
              <p className="text-gray-400 text-sm">Questo link invito è scaduto. Chiedi un nuovo invito al proprietario del progetto.</p>
            </div>
          )}

          {/* Usato */}
          {stato === 'usato' && (
            <div className="text-center py-4">
              <XCircle size={48} className="text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Link già utilizzato</h2>
              <p className="text-gray-400 text-sm">Questo link invito è già stato usato. Chiedi un nuovo invito al proprietario del progetto.</p>
            </div>
          )}

          {/* Errore */}
          {stato === 'errore' && (
            <div className="text-center py-4">
              <XCircle size={48} className="text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-white mb-2">Link non valido</h2>
              <p className="text-gray-400 text-sm">Questo link non esiste o è stato rimosso.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default Invito
